import { gsap } from 'gsap';
import React, { useRef, useEffect, useLayoutEffect, PropsWithChildren, FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import chroma from 'chroma-js';
import { Button, Icon } from '@column/column-ui-kit';
import { Sidebar } from './Sidebar';
import { Platform, User, Quicklinks } from './Toolbar';
import { SandboxBanner } from './SandboxBanner';
import { Logo } from '~/elements';
import { helpSidebars, modals } from '~/components';
import { useScrollPosition } from '~/hooks';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useSessionStore } from '~/stores/Session';

interface LocationState {
  from?: string;
}

const Wrapper = styled.div<{ $showSandboxBanner?: boolean }>`
  padding-left: 240px;
  height: 100vh;
  width: 100%;
  box-sizing: border-box;
  overflow-y: scroll;
  flex-grow: 1;
  transition: padding-top 0.15s;

  padding-top: ${({ $showSandboxBanner }) => $showSandboxBanner && '36px'};
`;

const AdditionalLogo = styled(Logo)<{ $showSandboxBanner?: boolean }>`
  position: fixed;
  left: 28px;
  top: ${({ $showSandboxBanner }) => ($showSandboxBanner ? '72px' : '36px')};
  z-index: 1;
`;

const Content = styled.div`
  --content-o: 0;
  opacity: var(--content-o);
  min-height: 100%;
  display: flex;
  flex-direction: column;
  will-change: opacity;
`;

const Toolbar = styled.div<{ isScrolled: boolean }>`
  padding: 16px 24px;
  position: sticky;
  top: 0;
  z-index: 20;
  margin-bottom: 32px;

  &:before,
  &:after {
    content: '';
    position: absolute;
    transition: transform 0.25s;
  }

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateZ(0);
    background-color: ${({ theme }) => chroma(theme.body).alpha(0.975).css()};
    transform-origin: 50% 0%;

    @supports ((-webkit-backdrop-filter: blur(8px)) or (backdrop-filter: blur(8px))) {
      backdrop-filter: blur(8px);
      background-color: ${({ theme }) => chroma(theme.body).alpha(0.9).css()};
    }
  }

  &:after {
    left: 0;
    right: 0;
    top: 100%;
    height: 1px;
    background-color: ${({ theme }) => theme.secondary.blendToBackground(150)};

    ${({ theme }) =>
      theme.id !== 'default' &&
      css`
        background-color: ${() => theme.secondary.blendToBackground(25)};
      `}
  }
`;

const ToolbarInner = styled.div<{ isScrolled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  z-index: 1;
  max-width: 1340px;
  margin: 0 auto;
  width: 100%;
`;

const Panel = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const Main = styled.div`
  padding: 0 24px 72px 24px;
  flex-grow: 1;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const Inner = styled.div`
  max-width: 1340px;
  margin: 0 auto;
  width: 100%;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const Help = styled(Button)`
  --button-border-radius: 50%;

  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 10;
  padding: 10px;

  svg {
    --icon-size: 20px;
  }
`;

export const Layout: FC<PropsWithChildren> = ({ children }) => {
  const { openHelpSidebar, openHelpSidebars } = useHelpSidebarStore();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<SVGSVGElement>(null);

  const { scrollY } = useScrollPosition(wrapperRef);
  const showSandboxBanner = useSessionStore((s) => s.showSandboxBanner);

  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as LocationState;

  useEffect(() => {
    if (state && state.from === 'register') {
      openHelpSidebar('Onboarding');
      navigate(location.pathname);
    }
    if (state && state.from === 'auth') {
      gsap.to(sidebarRef.current, {
        '--sidebar-o': 1,
        duration: 0.4,
        ease: 'none',
        onComplete: () => {
          navigate(location.pathname);
        },
      });
      gsap.to(contentRef.current, {
        '--content-o': 1,
        duration: 0.4,
        ease: 'none',
      });
    } else {
      gsap.set(sidebarRef.current, {
        '--sidebar-o': 1,
      });
      gsap.set(contentRef.current, {
        '--content-o': 1,
      });
    }
  }, [location]);

  useLayoutEffect(() => {
    if (!wrapperRef.current) {
      return;
    }
    wrapperRef.current.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <Wrapper ref={wrapperRef} id="scroll-container" $showSandboxBanner={showSandboxBanner}>
      <SandboxBanner />
      {state && state.from === 'auth' && (
        <AdditionalLogo $showSandboxBanner={showSandboxBanner} ref={logoRef} size="16px" variant="word" />
      )}
      <Sidebar ref={sidebarRef} />
      <Content ref={contentRef}>
        <Toolbar ref={toolbarRef} isScrolled={scrollY > 0 && openHelpSidebars.length < 1}>
          <ToolbarInner isScrolled={scrollY > 0 && openHelpSidebars.length < 1}>
            <Platform />
            <Panel>
              <Quicklinks />
              <User />
            </Panel>
          </ToolbarInner>
        </Toolbar>
        <Main>
          <Inner>{children}</Inner>
        </Main>
      </Content>
      {modals}
      {helpSidebars}
      <Help variant="secondary" onClick={() => openHelpSidebar('Support')}>
        <Icon.QuestionMark />
      </Help>
    </Wrapper>
  );
};
